import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Box,
  Button,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, useFormContext } from 'react-hook-form'
import { filter } from 'lodash'
import { useHistory, useLocation } from 'react-router-dom'
import { reverse } from 'named-urls'

import { Card, DriveTour } from 'components'
import { QuestionnaireForm } from 'views/ThirdPartyManagements/components'
import { ResponsibleEmailsTable, ShowQuestionnaires } from './components'

import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'
import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'
import { routes } from 'Routes'

const AuditForm = ({
  audit = null,
  openTour = false,
  setOpenTour,
  isEdit = false,
  isShow = false,
}) => {
  const history = useHistory()
  const location = useLocation()
  const snackbar = useSnackbar()
  const {
    isLoading,
    setIsLoading,
    companySupplierId,
    refresh,
    isPreAvaliation,
  } = useThirdPartyManagementStepper()

  const {
    control,
    errors,
    watch,
    handleSubmit,
    setValue,
    getValues,
    triggerValidation,
    register,
    setError,
    clearError,
  } = useFormContext()

  const fromPathname = location.state?.fromPathname
  const fromInitialStep = location.state?.fromInitialStep

  const navigateToEvaluations = () => {
    if (fromPathname) {
      history.push(fromPathname, { initialStep: fromInitialStep ?? 1 })
      return
    }

    const EVALUATIONS_STEP = isShow ? 2 : 1

    if (isShow) {
      history.push(
        reverse(routes.thirdPartyManagements.mySuppliers.show, {
          companySupplierId,
        }),
        { initialStep: EVALUATIONS_STEP },
      )
      return
    }

    history.push(
      reverse(routes.thirdPartyManagements.mySuppliers.edit, {
        companySupplierId,
      }),
      { initialStep: EVALUATIONS_STEP },
    )
  }

  const initialEmails =
    audit?.companySupplierAuditUsers?.map(
      (companySupplierAuditUser) => companySupplierAuditUser?.user?.email,
    ) || []

  const responsibleEmails = watch('responsibleEmails') || []
  const selectedQuizTemplates = watch('selectedQuizTemplates') || []

  const setResponsibleEmails = (next) => {
    const current = getValues('responsibleEmails') || []
    const value = typeof next === 'function' ? next(current) : next
    setValue('responsibleEmails', value)
  }

  const setSelectedQuizTemplates = (next) => {
    const current = getValues('selectedQuizTemplates') || []
    const value = typeof next === 'function' ? next(current) : next
    setValue('selectedQuizTemplates', value)
    if (value?.length > 0) clearError('selectedQuizTemplates')
  }

  const { STEPS, STEPS_OBJECT } = constants.tours.thirdPartyManagements

  const tourStepsMatrix = isPreAvaliation
    ? isShow
      ? STEPS_OBJECT.PRE_AVALIATION.SHOW.AUDIT
      : STEPS_OBJECT.PRE_AVALIATION.CREATION.AUDIT
    : isShow
    ? STEPS_OBJECT.SHOW.AUDIT
    : STEPS_OBJECT.CREATION.AUDIT

  const newResponsibleEmail = watch('newResponsibleEmail')
  const simplifiedRegister = watch('simplifiedRegister')

  register({ name: 'simplifiedRegister' })
  register({ name: 'responsibleEmails' })
  register({ name: 'selectedQuizTemplates' })

  const today = moment().startOf('day')

  const { pastBlockProps: dateToSendEmailsPastProps } =
    helpers.functions.datePickerPastBlocker(audit?.dateToSendEmails)
  const { pastBlockProps: endDatePastProps } =
    helpers.functions.datePickerPastBlocker(audit?.endDate)

  const handleAddResponsibleEmail = async () => {
    if (!newResponsibleEmail) return

    await triggerValidation('newResponsibleEmail')
    if (errors?.newResponsibleEmail) return

    const alreadyAdded = responsibleEmails.includes(newResponsibleEmail)
    if (alreadyAdded) {
      snackbar.open({ message: 'E-mail já adicionado', variant: 'error' })
      return
    }

    setResponsibleEmails([...responsibleEmails, newResponsibleEmail])
    setValue('newResponsibleEmail', '')
  }

  const isPastDateInvalid = (value, originalValue) => {
    if (!value) return false
    if (moment(value).isSameOrAfter(today)) return false
    return !(
      isEdit &&
      originalValue &&
      moment(originalValue).isBefore(today) &&
      moment(value).isSame(moment(originalValue), 'day')
    )
  }

  const onSubmit = async (data) => {
    if (isPastDateInvalid(data.dateToSendEmails, audit?.dateToSendEmails)) {
      setError('dateToSendEmails', 'min', 'A data não pode ser no passado')
      return
    }

    if (isPastDateInvalid(data.endDate, audit?.endDate)) {
      setError('endDate', 'min', 'A data não pode ser no passado')
      return
    }

    const apiCall = isEdit
      ? service.dponet.suppliers.audits.update
      : service.dponet.suppliers.audits.create

    try {
      setIsLoading(true)

      await apiCall({
        companySupplierId,
        ...(isEdit && { companySupplierAuditId: audit?.id }),
        responsibleEmails: data.responsibleEmails || [],
        quizTemplateIds: data.selectedQuizTemplates || [],
        endDate: helpers.thirdPartyManegementPartners.formatDate(data.endDate),
        dateToSendEmails: helpers.thirdPartyManegementPartners.formatDate(
          data.dateToSendEmails,
        ),
      })

      refresh()

      snackbar.open({
        message: isEdit
          ? 'Auditoria atualizada com sucesso'
          : 'Auditoria criada com sucesso',
        variant: 'success',
      })

      navigateToEvaluations()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar auditoria',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const removeResponsibleEmail = async (email) => {
    const responsibleEmailToRemove = email?.email ? email.email : email

    if (responsibleEmails.includes(responsibleEmailToRemove)) {
      const updatedResponsibleEmails = filter(
        responsibleEmails,
        (responsibleEmail) => responsibleEmail !== responsibleEmailToRemove,
      )

      setResponsibleEmails(updatedResponsibleEmails)
    }
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={24}>
      <Card
        id={
          isPreAvaliation
            ? isShow
              ? STEPS.PRE_AVALIATION.SHOW.AUDIT[1]
              : STEPS.PRE_AVALIATION.CREATION.AUDIT[1]
            : isShow
            ? STEPS.SHOW.AUDIT[1]
            : STEPS.CREATION.AUDIT[1]
        }
        title="Responsáveis"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Controller
              as={
                <KeyboardDatePicker
                  fullWidth
                  format="DD/MM/yyyy"
                  label={`Quando os responsáveis devem ser notificados ${
                    simplifiedRegister ? '' : '*'
                  }`}
                  helperText={errors?.dateToSendEmails?.message}
                  error={!!errors?.dateToSendEmails}
                  invalidDateMessage="Data inválida"
                  {...dateToSendEmailsPastProps}
                  disabled={isShow}
                />
              }
              defaultValue={null}
              control={control}
              name="dateToSendEmails"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              as={
                <KeyboardDatePicker
                  fullWidth
                  format="DD/MM/yyyy"
                  label="Estipule um prazo para que eles respondam aos questionários"
                  helperText={errors?.endDate?.message}
                  error={!!errors?.endDate}
                  invalidDateMessage="Data inválida"
                  {...endDatePastProps}
                  disabled={isShow}
                />
              }
              defaultValue={null}
              control={control}
              name="endDate"
            />
          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item xs={12}>
                <Typography variant="h5">
                  E-mail dos Responsáveis {simplifiedRegister ? '' : '*'}
                </Typography>
              </Grid>
              {!isShow && (
                <>
                  <Grid item xs={9} md={10}>
                    <Controller
                      as={
                        <TextField
                          fullWidth
                          placeholder="E-mail de um responsável para responder os questionários"
                          type="email"
                          helperText={errors?.newResponsibleEmail?.message}
                          error={!!errors?.newResponsibleEmail}
                        />
                      }
                      control={control}
                      name="newResponsibleEmail"
                    />
                  </Grid>
                  <Grid item xs={3} md={2}>
                    <Button
                      onClick={handleAddResponsibleEmail}
                      color="primary"
                      variant="contained"
                      type="button"
                    >
                      Adicionar
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <ResponsibleEmailsTable
              responsibleEmails={responsibleEmails}
              removeResponsibleEmail={removeResponsibleEmail}
              initialEmails={initialEmails}
              audit={audit}
              isShow={isShow}
            />
            {!!errors?.responsibleEmails && (
              <FormHelperText error>
                {errors.responsibleEmails.message}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </Card>
      {isShow ? (
        <Grid container spacing={3}>
          <Grid
            id={
              isPreAvaliation
                ? STEPS.PRE_AVALIATION.SHOW.AUDIT[2]
                : STEPS.SHOW.AUDIT[2]
            }
            item
            xs={12}
          >
            <ShowQuestionnaires />
          </Grid>
        </Grid>
      ) : (
        <Card title="Questionários *">
          <Grid container spacing={3}>
            <Grid
              id={
                isPreAvaliation
                  ? STEPS.PRE_AVALIATION.CREATION.AUDIT[2]
                  : STEPS.CREATION.AUDIT[2]
              }
              item
              xs={12}
            >
              <QuestionnaireForm
                selectedQuizTemplates={selectedQuizTemplates}
                setSelectedQuizTemplates={setSelectedQuizTemplates}
                linkedQuestionnaires={
                  audit?.companySupplierQuestionnaires || []
                }
                isEdit={isEdit}
                isShow={isShow}
              />
              {!!errors?.selectedQuizTemplates && (
                <FormHelperText error>
                  {errors.selectedQuizTemplates.message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Card>
      )}

      <Box display="flex" justifyContent="flex-end" gridGap={8}>
        <Button variant="outlined" onClick={navigateToEvaluations}>
          {isShow ? 'Voltar' : 'Cancelar'}
        </Button>
        {!isShow && (
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {isEdit ? 'Salvar' : 'Criar Auditoria'}
          </Button>
        )}
      </Box>

      <DriveTour
        stepsMatrix={tourStepsMatrix}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Box>
  )
}

AuditForm.propTypes = {
  audit: PropTypes.object,
  openTour: PropTypes.bool,
  setOpenTour: PropTypes.func,
  isEdit: PropTypes.bool,
  isShow: PropTypes.bool,
}

export default AuditForm
